import axios from "axios";
import { getApiBaseUrl, calSign, isDevEnv, isTestEnv, localizeBusinessError } from "@/utils/tools";
import ALL_URL, { MOCK_CONFIG, MOCK_URL_METHOD } from "@/api/url";

/** Mock 响应延迟(ms)，仅 dev 环境，用于模拟真实网络耗时 */
const MOCK_DELAY_MS = 300;

const instance = axios.create({
    baseURL: getApiBaseUrl(),
    headers: {
        "Content-Type": "application/json"
    },
    timeout: 60000
});

instance.interceptors.request.use(config => {
    config.data = { source: 2, ...config.data };

    const accessToken = localStorage.getItem('accessToken') || null;
    config.headers['Authorization'] = calSign(config.url, config.data, accessToken);

    if (isDevEnv() || isTestEnv()) {
        const env = isDevEnv() ? '开发' : '测试';
        console.log(`[${env}环境] 请求:`, { url: config.url, data: config.data });
    }

    return config;
});

instance.interceptors.response.use(
    res => {
        // 业务结果若 errorCode 命中映射表，替换 errorMsg 为友好中文提示
        if (!res.config.responseType || res.config.responseType !== 'blob') {
            localizeBusinessError(res.data);
        }
        return res.config.responseType === 'blob' ? res : res.data;
    },
    error => Promise.reject(error)
);

async function request(config) {
    // 下载文件模式
    const isDownload = config.isDownload;
    if (isDownload) {
        config.responseType = 'blob';
        delete config.isDownload;
    }

    if (import.meta.env.DEV) {
        const method = (config.method || "get").toLowerCase();

        // 支持 :param 动态路径模板（如 /tasks/:task_id）
        const isPathMatch = (path) => {
            if (typeof path !== "string") return false;
            if (!path.includes(":")) return path === config.url;
            const pattern = new RegExp(`^${path.replace(/:[^/]+/g, "[^/]+")}$`);
            return pattern.test(config.url);
        };
        const matchedEntries = Object.entries(ALL_URL).filter(([, path]) => isPathMatch(path));
        // 同一路径模板对应多个 key 时（如 GET/DELETE /tasks/:task_id），按请求方法消歧
        const entry = matchedEntries.find(([key]) => MOCK_URL_METHOD[key] === method)
            || (matchedEntries.length === 1 ? matchedEntries[0] : undefined);
        const key = entry?.[0];

        if (key && MOCK_CONFIG[key]) {
            const { default: mockData } = await import("@/api/mock");
            const mockResp = mockData[key];
            if (mockResp) {
                console.log(`[Mock] ${method.toUpperCase()} ${config.url}`, mockResp);
                // 模拟网络延迟
                await new Promise((resolve) => setTimeout(resolve, MOCK_DELAY_MS));
                return mockResp;
            }
        }
    }

    const res = await instance(config);
    // 下载模式返回完整 response（含 blob 和 headers）
    return isDownload ? res : res;
}

export default request;
